import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { z } from 'zod'

/**
 * The customer population filters — the vocabulary the Credit Customers list and New Mass Review
 * share. The Customers page keeps them in its URL; the mass page reads them from its URL on
 * arrival (the quick action from the Customers page) and applies them locally. This module is the
 * pure, testable side: the search schema, its key lists, and the conversion to the JSON body the
 * mass endpoints expect. The hook that turns them into filter controls is
 * `hooks/filters/credit/useCustomerPopulationFilters`.
 */

export const POPULATION_ARRAY_KEYS = [
  'erp',
  'accountGroup',
  'creditGroup',
  'region',
  'legalName',
  'riskClass',
  'recommendedRiskClass',
  'scoreTier',
  'equifaxCode',
  'duns',
  'overallBlock',
  'paymentTerms',
  'payerType',
  'creditBlock',
  'paymentMethod',
] as const
export type PopulationArrayKey = (typeof POPULATION_ARRAY_KEYS)[number]

export const POPULATION_NUMBER_KEYS = [
  'creditLimitMin',
  'creditLimitMax',
  'failedPmtMin',
  'failedPmtMax',
  'securityDepositsMin',
  'securityDepositsMax',
  'itcoErrorsMin',
  'itcoErrorsMax',
  'paymentsPerWeekMin',
  'paymentsPerWeekMax',
  'ordersPerWeekMin',
  'ordersPerWeekMax',
  'activeSitesMin',
  'activeSitesMax',
] as const
export type PopulationNumberKey = (typeof POPULATION_NUMBER_KEYS)[number]

export const POPULATION_DATE_KEYS = [
  'nextReviewDateFrom',
  'nextReviewDateTo',
  'createdFrom',
  'createdTo',
  'lastFailedPaymentFrom',
  'lastFailedPaymentTo',
] as const
export type PopulationDateKey = (typeof POPULATION_DATE_KEYS)[number]

const keyed = <TKey extends string, TSchema extends z.ZodTypeAny>(
  keys: readonly TKey[],
  schema: TSchema,
) => Object.fromEntries(keys.map((key) => [key, schema])) as Record<TKey, TSchema>

/** The population filters as route search params (the Customers list URL, the mass page's seed). */
export const populationSearchSchema = z.object({
  keyword: z.string().optional(),
  ...keyed(POPULATION_ARRAY_KEYS, z.array(z.string()).optional()),
  ...keyed(POPULATION_NUMBER_KEYS, z.number().optional()),
  ...keyed(POPULATION_DATE_KEYS, z.coerce.date().optional()),
})
export type PopulationSearch = z.infer<typeof populationSearchSchema>

export const POPULATION_SEARCH_KEYS = Object.keys(populationSearchSchema.shape) as Array<
  keyof PopulationSearch
>

/** Every population key set to undefined — spread into `navigate({ search })` to clear the URL. */
export const CLEARED_POPULATION_SEARCH = Object.fromEntries(
  POPULATION_SEARCH_KEYS.map((key) => [key, undefined]),
) as Record<keyof PopulationSearch, undefined>

/** The population subset of a wider route search (drops page, pageSize, sort, ...). */
export function pickPopulationSearch(search: Record<string, unknown>): PopulationSearch {
  return Object.fromEntries(
    POPULATION_SEARCH_KEYS.flatMap((key) => (key in search ? [[key, search[key]]] : [])),
  ) as PopulationSearch
}

/**
 * True when any population value is set: a non-empty array, a non-empty string, a number or a
 * date. Accepts either wire shape (search params or the mass body), so it answers both "does the
 * URL carry a filter" and "is anything applied".
 */
export function hasAnyPopulationFilter(values: Record<string, unknown>): boolean {
  return POPULATION_SEARCH_KEYS.some((key) => {
    const value = values[key]
    if (value === undefined || value === null) return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'string') return value.length > 0
    return true
  })
}

/**
 * The population filters as the mass create/preview endpoints expect them: arrays always present,
 * dates as 'YYYY-MM-DD'.
 */
export type MassClrSelectionFilters = Omit<
  PopulationSearch,
  PopulationArrayKey | PopulationDateKey
> &
  Record<PopulationArrayKey, string[]> &
  Partial<Record<PopulationDateKey, string>>

const toDateOnly = (date: Date | string | undefined) =>
  date === undefined ? undefined : typeof date === 'string' ? date : formatDateOnly(date)

/**
 * The body the mass endpoints expect. Dates go through `formatDateOnly` (local time), like the
 * Customers list — `toISOString` would slip an evening date to the next day east of UTC.
 */
export function toMassClrSelectionFilters(
  search: Partial<PopulationSearch>,
): MassClrSelectionFilters {
  return {
    keyword: search.keyword || undefined,
    ...(Object.fromEntries(POPULATION_ARRAY_KEYS.map((key) => [key, search[key] ?? []])) as Record<
      PopulationArrayKey,
      string[]
    >),
    ...(Object.fromEntries(POPULATION_NUMBER_KEYS.map((key) => [key, search[key]])) as Partial<
      Record<PopulationNumberKey, number>
    >),
    ...(Object.fromEntries(
      POPULATION_DATE_KEYS.map((key) => [key, toDateOnly(search[key])]),
    ) as Partial<Record<PopulationDateKey, string>>),
  }
}

export const EMPTY_MASS_CLR_SELECTION: MassClrSelectionFilters = toMassClrSelectionFilters({})
